import {AsyncButton} from "@client/components/Button";
import {DeleteButtonLabel, useMultiStateDelete} from "@client/components/DeleteButton";
import {BarSurface} from "@client/components/Glass";
import type {IconType} from "@client/components/Icon";
import {SimpleIcon} from "@client/components/Icon";
import {Shared} from "@client/gotterdammerung";
import * as hardware from "@client/hardware";
import {CheckIcon, PencilIcon} from "@heroicons/react/16/solid";
import {EllipsisVerticalIcon, XMarkIcon} from "@heroicons/react/20/solid";
import {classNames, server} from "@reactivated";
import {AnimatePresence, type Transition, motion} from "motion/react";
import React from "react";

// The journal's Workout card and the Complete pill: Start sends the card into the
// pill, Complete brings it back out. The classes are in input.css.
export const WORKOUT_CARD = {
    name: "workout-card",
    share: {start: "joy-morph-to-pill", complete: "joy-morph-to-card"},
} as const;

type Handlers = Omit<React.ComponentProps<typeof WorkoutBar>, "completed">;

// The workout tab declares its bar in the scene's bottom section, so the bar
// commits with the tab and Start's card has a pill to land in. Its handlers live
// in the workout, which registers them a commit later.
let handlers: Handlers | null = null;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

const UNREGISTERED: Handlers = {
    actions: [],
    onDelete: async () => {},
    disabled: false,
    onComplete: async () => {},
    onEdit: () => {},
    steps: null,
};

export function useWorkoutBar(next: Handlers) {
    React.useLayoutEffect(() => {
        handlers = next;
        listeners.forEach((listener) => listener());
    });
    React.useLayoutEffect(
        () => () => {
            handlers = null;
            listeners.forEach((listener) => listener());
        },
        [],
    );
}

const ITEM_SPRING: Transition = {type: "spring", stiffness: 400, damping: 22};
const BUTTON_SPRING: Transition = {type: "spring", stiffness: 500, damping: 30};
const STAGGER = 0.05;

interface MenuAction {
    icon: IconType;
    title: string;
    onClick: () => void;
}

function MenuItem(props: {index: number; children: React.ReactNode}) {
    return (
        <motion.div
            className="flex items-center gap-2.5"
            initial={{opacity: 0, y: 24, scale: 0.6}}
            animate={{opacity: 1, y: 0, scale: 1}}
            exit={{opacity: 0, y: 16, scale: 0.8, transition: {duration: 0.15}}}
            transition={{...ITEM_SPRING, delay: props.index * STAGGER}}
            style={{transformOrigin: "20px 50%"}}
        >
            {props.children}
        </motion.div>
    );
}

const circle =
    "flex size-10 flex-none items-center justify-center rounded-full bg-white shadow-[0_4px_14px_rgba(28,27,34,0.14)]";
const chip =
    "rounded-lg bg-white px-3 py-1.5 text-sm font-semibold whitespace-nowrap text-stone-900 shadow-[0_4px_14px_rgba(28,27,34,0.14)]";

function DeleteItem(props: {
    index: number;
    onDelete: () => Promise<void>;
    onClose: () => void;
}) {
    const {state, handleClick} = useMultiStateDelete({
        onDelete: props.onDelete,
        onClose: props.onClose,
    });
    const tone = {
        idle: ["text-red-600", "text-stone-900"],
        confirm: ["bg-red-500! text-white", "bg-red-500! text-white"],
        processing: ["bg-stone-400! text-white", "bg-stone-400! text-white"],
        success: ["bg-green-500! text-white", "bg-green-500! text-white"],
        error: ["bg-red-600! text-white", "bg-red-600! text-white"],
    }[state];

    return (
        <MenuItem index={props.index}>
            <button
                type="button"
                onClick={handleClick}
                disabled={state === "processing" || state === "success"}
                className="flex items-center gap-2.5"
            >
                <span className={classNames(circle, "transition-colors", tone[0])}>
                    <SimpleIcon icon="0018-eraser" className="size-4" />
                </span>
                <span
                    className={classNames(
                        chip,
                        "flex overflow-hidden transition-colors",
                        tone[1],
                    )}
                >
                    <DeleteButtonLabel state={state} />
                </span>
            </button>
        </MenuItem>
    );
}

// Rare actions behind one button at the left of the bar; the primary action keeps
// the right, under the thumb.
function ActionsButton(props: {
    actions: MenuAction[];
    onDelete: (menuClosed: Promise<void>) => Promise<void>;
}) {
    const [open, setOpen] = React.useState(false);
    const close = () => setOpen(false);
    const exited = React.useRef<() => void>(null);
    // A confirmed delete closes the menu at once; the deletion and its slide run
    // while the menu leaves, and the slide waits for it to be gone.
    const confirmDelete = () => {
        const menuClosed = new Promise<void>((resolve) => {
            exited.current = resolve;
        });
        close();
        return props.onDelete(menuClosed);
    };
    const toggle = (
        <button
            type="button"
            aria-label={open ? "Close workout actions" : "Workout actions"}
            aria-expanded={open}
            onClick={() => {
                void hardware.haptic();
                setOpen(!open);
            }}
            className="flex size-10 items-center justify-center"
        >
            <motion.span
                key={open ? "close" : "open"}
                className="flex text-chrome-ink"
                initial={{rotate: open ? -90 : 90, opacity: 0}}
                animate={{rotate: 0, opacity: 1}}
                transition={BUTTON_SPRING}
            >
                {open ? (
                    <XMarkIcon className="size-5" />
                ) : (
                    <EllipsisVerticalIcon className="size-5" />
                )}
            </motion.span>
        </button>
    );

    return (
        <div className="relative">
            <AnimatePresence onExitComplete={() => exited.current?.()}>
                {open && (
                    <motion.div
                        key="scrim"
                        className="fixed inset-0 bg-stone-900/25 backdrop-blur-[3px]"
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.2}}
                        onClick={close}
                    />
                )}
            </AnimatePresence>
            <div className="absolute bottom-full left-0 mb-3 flex flex-col-reverse items-start gap-3">
                <AnimatePresence>
                    {open &&
                        props.actions.map((action, index) => (
                            <MenuItem key={action.title} index={index}>
                                <button
                                    type="button"
                                    className="flex items-center gap-2.5"
                                    onClick={() => {
                                        void hardware.haptic();
                                        close();
                                        action.onClick();
                                    }}
                                >
                                    <span
                                        className={classNames(
                                            circle,
                                            "text-indigo-700",
                                        )}
                                    >
                                        <SimpleIcon
                                            icon={action.icon}
                                            className="size-4"
                                        />
                                    </span>
                                    <span className={chip}>{action.title}</span>
                                </button>
                            </MenuItem>
                        ))}
                    {open && (
                        <DeleteItem
                            key="delete"
                            index={props.actions.length}
                            onDelete={confirmDelete}
                            onClose={close}
                        />
                    )}
                </AnimatePresence>
            </div>
            {open ? (
                // Solid while open: the glass surface reads gray over the scrim.
                <div className={classNames(circle, "relative")}>{toggle}</div>
            ) : (
                <BarSurface className="relative flex-none rounded-full">
                    {toggle}
                </BarSurface>
            )}
        </div>
    );
}

// The day's steps beside the actions: a readout, nothing to tap.
function StepsBubble(props: {steps: number}) {
    const label = Math.round(props.steps).toLocaleString();
    return (
        <BarSurface className="flex-none rounded-full">
            <div
                role="img"
                aria-label={`${label} steps`}
                className="flex h-10 items-center gap-1.5 pr-3.5 pl-3 text-sm font-medium text-chrome-ink tabular-nums"
            >
                <SimpleIcon icon="0302-walk" className="size-4 text-[#EF8165]" />
                {label}
            </div>
        </BarSurface>
    );
}

// Both labels sit in one grid cell and the hidden one keeps its width, so the
// pill is one size whether the workout is open or completed.
export function WorkoutBar(props: {
    actions: MenuAction[];
    onDelete: (menuClosed: Promise<void>) => Promise<void>;
    completed: boolean;
    disabled: boolean;
    onComplete: () => Promise<void>;
    onEdit: () => void;
    steps: number | null;
}) {
    const {completed} = props;
    const label =
        "col-start-1 row-start-1 flex items-center justify-center gap-1.5 transition-opacity duration-200";
    return (
        <div className="relative flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
                <ActionsButton actions={props.actions} onDelete={props.onDelete} />
                {props.steps != null && <StepsBubble steps={props.steps} />}
            </div>
            <Shared {...WORKOUT_CARD}>
                <div data-complete className="flex-none rounded-full">
                    <BarSurface tone="primary" className="flex-none rounded-full">
                        <AsyncButton
                            onClick={async () => {
                                void hardware.haptic();
                                if (completed) {
                                    props.onEdit();
                                } else {
                                    await props.onComplete();
                                }
                            }}
                            disabled={props.disabled}
                            className="grid h-10 items-center px-5 text-sm font-medium text-chrome-ink-strong disabled:opacity-50"
                        >
                            <span
                                aria-hidden={completed}
                                className={classNames(label, completed && "opacity-0")}
                            >
                                <CheckIcon className="size-4" />
                                Complete workout
                            </span>
                            <span
                                aria-hidden={!completed}
                                className={classNames(label, !completed && "opacity-0")}
                            >
                                <PencilIcon className="size-4" />
                                Edit workout
                            </span>
                        </AsyncButton>
                    </BarSurface>
                </div>
            </Shared>
        </div>
    );
}

export function WorkoutBarSlot(props: {
    workout: Pick<
        NonNullable<server.journal.day.Journal.output["workout"]>,
        "status" | "mode"
    > | null;
}) {
    const registered = React.useSyncExternalStore(subscribe, () => handlers);
    // A guided workout portals its own menu; no workout yet has no bar.
    if (props.workout == null || props.workout.mode == "GUIDED") {
        return null;
    }
    return (
        <WorkoutBar
            completed={props.workout.status == "COMPLETE"}
            {...(registered ?? UNREGISTERED)}
        />
    );
}
